import { useEffect, useState } from 'react'
import type { Redirect } from '@nhanho/shared'
import { formatDateVN } from '@nhanho/shared'
import { redirects as api, explainError } from '../../lib/api'
import { useList } from '../../lib/useList'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'

interface Form { from: string; to: string; code: 301 | 302; note: string }
const EMPTY: Form = { from: '', to: '', code: 301, note: '' }

export default function RedirectList() {
  const { data, loading, params, set, refresh } = useList<Redirect>((p) =>
    api.list({ limit: 20, sort: '-updatedAt', ...p }),
  )
  const toast = useToast()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmDel, setConfirmDel] = useState(false)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [form, setForm] = useState<Form>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [q, setQ] = useState(String(params.search ?? ''))
  const dq = useDebounced(q, 300)
  useEffect(() => { if (dq !== String(params.search ?? '')) set({ search: dq }) }, [dq]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setSelected(new Set()), [data])

  const openNew = () => { setEditing('new'); setForm(EMPTY); setErrors({}) }
  const openEdit = (r: Redirect) => {
    setEditing(r.id)
    setForm({ from: r.from, to: r.to, code: r.code, note: r.note ?? '' })
    setErrors({})
  }

  const save = async () => {
    setBusy(true); setErrors({})
    try {
      if (editing === 'new') await api.create(form)
      else if (editing) await api.update(editing, form)
      toast.success('Đã lưu điều hướng')
      setEditing(null)
      refresh()
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
    } finally { setBusy(false) }
  }

  const allChecked = !!data?.docs.length && data.docs.every((d) => selected.has(d.id))
  const bulkDelete = async () => {
    if (!selected.size) return
    if (!confirmDel) return setConfirmDel(true)
    setBusy(true)
    try {
      const r = await api.bulk([...selected])
      toast.success(`Đã xoá ${r.affected} điều hướng`)
      setConfirmDel(false)
      refresh()
    } catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader
        title="Điều hướng"
        subtitle={data ? `${data.totalDocs} đường dẫn cũ đang được chuyển tiếp` : undefined}
        actions={<Button onClick={openNew}>+ Thêm điều hướng</Button>}
      />

      <Card className="mb-4">
        <p className="text-sm text-ink-muted">
          Khi bạn đổi slug của một sản phẩm, bài viết, danh mục hay trang <strong>đang hiển thị</strong>,
          hệ thống <strong>tự tạo</strong> một dòng ở đây (nhãn <em>Tự động</em>) để link cũ không thành 404.
          Thêm tay khi cần chuyển hướng một đường dẫn không thuộc trường hợp đó.
        </p>
      </Card>

      <div className="mb-4 grid gap-2 md:grid-cols-[1fr_160px]">
        <Input placeholder="Tìm đường dẫn cũ, đích, ghi chú…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={String(params.source ?? '')} onChange={(e) => set({ source: e.target.value || null })}>
          <option value="">Mọi nguồn</option><option value="auto">Tự động</option><option value="manual">Thêm tay</option>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-button bg-primary-100 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} đã chọn</span>
          <Button size="sm" variant="danger" loading={busy} onClick={bulkDelete}>Xoá</Button>
        </div>
      )}

      {loading && !data ? <Spinner /> : !data?.docs.length ? (
        <EmptyState
          title="Chưa có điều hướng nào"
          hint="Bình thường — danh sách này chỉ đầy lên khi có slug được đổi."
          action={<Button onClick={openNew}>+ Thêm điều hướng</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-sand text-left text-xs uppercase tracking-wider text-ink-muted"><tr>
              <th className="w-10 px-3 py-2.5">
                <input type="checkbox" checked={allChecked} onChange={() => setSelected(allChecked ? new Set() : new Set(data.docs.map((d) => d.id)))} aria-label="Chọn tất cả" />
              </th>
              <th className="px-3 py-2.5">Đường dẫn cũ → đích</th>
              <th className="px-3 py-2.5">Mã</th>
              <th className="px-3 py-2.5">Nguồn</th>
              <th className="px-3 py-2.5">Lượt dùng</th>
              <th className="px-3 py-2.5">Sửa lần cuối</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {data.docs.map((r) => (
                <tr key={r.id} className={selected.has(r.id) ? 'bg-primary-50' : 'hover:bg-sand/60'}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => setSelected((s) => { const n = new Set(s); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n })} aria-label={`Chọn ${r.from}`} />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => openEdit(r)} className="block min-w-0 text-left">
                      <div className="truncate font-mono text-xs text-ink hover:underline">{r.from}</div>
                      <div className="truncate font-mono text-xs text-ink-muted">↳ {r.to}</div>
                      {r.note && <div className="truncate text-xs text-ink-muted">{r.note}</div>}
                    </button>
                  </td>
                  <td className="px-3 py-2"><Badge tone={r.code === 301 ? 'success' : 'neutral'}>{r.code}</Badge></td>
                  <td className="px-3 py-2 text-ink-muted">{r.source === 'auto' ? 'Tự động' : 'Thêm tay'}</td>
                  {/* `hits` là cách duy nhất biết dòng nào còn ai dùng mà dọn — 0 lâu ngày là xoá được. */}
                  <td className="px-3 py-2 text-ink-muted">{r.hits ?? 0}</td>
                  <td className="px-3 py-2 text-ink-muted">{formatDateVN(r.updatedAt) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && <div className="border-t border-border px-3 py-2.5"><Pagination data={data} onPage={(p) => set({ page: p })} /></div>}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Thêm điều hướng' : 'Sửa điều hướng'}
        footer={<><Button variant="secondary" onClick={() => setEditing(null)}>Huỷ</Button><Button onClick={save} loading={busy}>Lưu</Button></>}
      >
        <div className="space-y-4">
          <Field label="Đường dẫn cũ" required hint="Tính từ gốc site, có dấu / đầu. Ví dụ: /danh-muc/moc-khoa" error={errors.from}>
            <Input value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} className="font-mono" placeholder="/duong-dan-cu" autoFocus />
          </Field>
          <Field label="Đích" required hint="Đường dẫn nội bộ (/san-pham/abc) hoặc URL đầy đủ ra ngoài" error={errors.to}>
            <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="font-mono" placeholder="/san-pham/moc-khoa" />
          </Field>
          <Field label="Loại chuyển hướng" hint="301 chuyển luôn thứ hạng SEO sang đích mới và được trình duyệt nhớ rất lâu. Chỉ dùng 302 khi thật sự là tạm thời.">
            <Select value={String(form.code)} onChange={(e) => setForm({ ...form, code: Number(e.target.value) === 302 ? 302 : 301 })}>
              <option value="301">301 — vĩnh viễn</option>
              <option value="302">302 — tạm thời</option>
            </Select>
          </Field>
          <Field label="Ghi chú" hint="Vì sao có dòng này — người sau (hoặc chính bạn 3 tháng nữa) sẽ cần" error={errors.note}>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDel}
        onCancel={() => setConfirmDel(false)}
        onConfirm={bulkDelete}
        loading={busy}
        title="Xoá điều hướng đã chọn?"
        message={<>Xoá <strong>{selected.size}</strong> dòng. Các đường dẫn cũ đó sẽ trả về 404 trở lại.</>}
      />
    </>
  )
}
